import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SplitLines } from "@/components/ui-custom/Reveal";
import { Seo, breadcrumbSchema } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { CTABand } from "@/components/home/CTABand";
import { SERVICES } from "@/content/services";
import { resolveUrl } from "@/lib/api";

const splitHeading = (t) => {
  const w = t.split(" "); const mid = Math.ceil(w.length / 2);
  return [w.slice(0, mid).join(" "), w.slice(mid).join(" ")];
};

export const FaqList = ({ faqs, testId = "faq" }) => (
  <Accordion type="single" collapsible className="divide-y divide-[var(--sand)] border-y border-[var(--sand)]" data-testid={testId}>
    {faqs.map((f, i) => (
      <AccordionItem key={i} value={`faq-${i}`} className="border-0">
        <AccordionTrigger className="text-left font-display text-lg sm:text-xl font-normal py-5 hover:no-underline hover:text-[var(--terracotta)]" data-testid={`${testId}-q-${i}`}>{f.q}</AccordionTrigger>
        <AccordionContent className="text-[var(--charcoal)] font-light text-base leading-relaxed pb-6">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const ServiceLinks = ({ slugs, exclude }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {slugs.filter((s) => s !== exclude).map((s) => (
      <Link key={s} to={SERVICES[s].path} className="group border border-[var(--sand)] rounded-sm p-6 hover:border-[var(--ink)] transition-colors" data-testid={`service-link-${s}`}>
        <p className="eyebrow text-[var(--terracotta)]">{SERVICES[s].eyebrow}</p>
        <p className="font-display text-xl mt-2 text-[var(--ink)] flex items-start justify-between gap-3">{SERVICES[s].h1}<ArrowUpRight size={18} className="shrink-0 mt-1 opacity-50 group-hover:opacity-100 group-hover:text-[var(--terracotta)] transition-all" /></p>
      </Link>
    ))}
  </div>
);

export default function ServicePage({ slug }) {
  const s = SERVICES[slug];
  const crumbs = [{ to: "/#services", label: "Services" }, { to: s.path, label: s.navLabel }];
  const faqSchema = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: s.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  const serviceSchema = { "@context": "https://schema.org", "@type": "Service", name: s.h1, provider: { "@id": `${process.env.REACT_APP_BACKEND_URL}/#organization` }, areaServed: "Sydney, NSW, ACT, South East Queensland", description: s.metaDescription };

  return (
    <main data-testid={`service-page-${slug}`}>
      <Seo title={s.seoTitle} description={s.metaDescription} image={s.image} schema={[breadcrumbSchema(crumbs), faqSchema, serviceSchema]} />
      <section className="pt-32 sm:pt-40 pb-16 lg:pb-24 mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Breadcrumbs items={crumbs} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-10 items-start">
          <div className="lg:col-span-7 lg:pt-6">
            <Reveal><p className="eyebrow text-[var(--terracotta)] mb-6">{s.eyebrow}</p></Reveal>
            <SplitLines lines={splitHeading(s.h1)} className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[var(--ink)]" data-testid="service-h1" />
            <Reveal delay={0.3}><p className="mt-8 text-base md:text-lg text-[var(--charcoal)] font-light leading-relaxed max-w-2xl" data-testid="service-intro">{s.intro}</p></Reveal>
            <Reveal delay={0.4}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/become-a-stockist?interest=${slug}`} className="btn-pill btn-ink" data-testid="service-cta">{s.cta} <ArrowUpRight size={14} /></Link>
                <Link to="/brands" className="btn-pill btn-outline">Browse the range <ArrowRight size={14} /></Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}><div className="img-frame aspect-[4/3] lg:aspect-[4/4.4] rounded-sm"><img src={resolveUrl(s.image)} alt={s.imageAlt} className="absolute inset-0" /></div></Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pb-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-14">
            {s.sections.map((sec, i) => (
              <Reveal key={i} className="grid sm:grid-cols-[3rem_1fr] gap-4">
                <span className="font-display text-[var(--terracotta)] text-lg">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="font-display font-normal text-2xl sm:text-3xl text-[var(--ink)] leading-tight">{sec.h2}</h2>
                  {sec.paragraphs.map((p, j) => <p key={j} className="mt-5 text-[var(--charcoal)] font-light leading-relaxed">{p}</p>)}
                </div>
              </Reveal>
            ))}
          </div>
          <aside className="lg:col-span-4 lg:col-start-9 space-y-10">
            <Reveal>
              <p className="eyebrow text-[var(--muted)] mb-4">Related ranges</p>
              <ul className="space-y-2">
                {s.relatedCategories.map(([slug2, label]) => (
                  <li key={slug2}><Link to={`/range/${slug2}`} className="link-underline text-[var(--ink)] font-medium" data-testid={`service-category-${slug2}`}>{label} ranges <ArrowRight size={12} className="inline ml-1" /></Link></li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="eyebrow text-[var(--muted)] mb-4">Related services</p>
              <ServiceLinks slugs={s.relatedServices} exclude={slug} />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-[var(--ink)] text-[var(--cream)] rounded-sm p-6">
                <p className="eyebrow text-[var(--stone)]">Based in Sydney</p>
                <p className="mt-3 font-light text-sm leading-relaxed text-[var(--cream)]/85">39 Sunblest Crescent, Mount Druitt NSW 2770. Servicing Sydney, NSW, ACT and south-east Queensland on our own refrigerated fleet.</p>
                <Link to="/contact" className="link-underline text-sm mt-4 inline-block">Contact details</Link>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <section className="bg-[var(--cream-2)] py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><Reveal><p className="eyebrow text-[var(--terracotta)]">Common questions</p><h2 className="font-display font-normal text-3xl sm:text-4xl mt-3 text-[var(--ink)]">Frequently asked</h2></Reveal></div>
          <div className="lg:col-span-8"><Reveal delay={0.1}><FaqList faqs={s.faqs} testId="service-faq" /></Reveal></div>
        </div>
      </section>
      <CTABand />
    </main>
  );
}
